import type { RequestFn } from '../client';
import type {
  PaypalDisputeCase,
  PaypalDisputeListRequest,
  PaypalDisputeImportRequest,
  PaypalDisputeImportResponse,
  PaypalDisputeActionRequest,
  PaypalDisputeActionResponse,
  PaypalDisputeActionRecord,
  PaypalDisputeDocument,
  PaypalDisputeDownloadRequest,
} from '../types';

/**
 * PayPal seller cases, including external and subscription sales.
 * Every operation honors the caller's connector-account grants.
 */
export class PaypalDisputes {
  constructor(private readonly request: RequestFn) {}
  actions(caseId: string, offset = 0): Promise<PaypalDisputeActionRecord[]> {
    return this.request('GET', `/disputes/paypal/cases/${encodeURIComponent(caseId)}/actions`, {
      query: { offset },
    });
  }
  download(caseId: string, documentUrl: string): Promise<PaypalDisputeDocument> {
    const body: PaypalDisputeDownloadRequest = { document_url: documentUrl };
    return this.request(
      'POST',
      `/disputes/paypal/cases/${encodeURIComponent(caseId)}/documents/download`,
      { body },
    );
  }
  list(params?: PaypalDisputeListRequest): Promise<PaypalDisputeCase[]> {
    return this.request('GET', '/disputes/paypal/cases', {
      query: params as Record<string, string | number | boolean | undefined>,
    });
  }
  retrieve(caseId: string, forceSync = false): Promise<PaypalDisputeCase> {
    return this.request('GET', `/disputes/paypal/cases/${encodeURIComponent(caseId)}`, {
      query: { force_sync: forceSync },
    });
  }
  history(caseId: string, offset = 0): Promise<PaypalDisputeCase[]> {
    return this.request('GET', `/disputes/paypal/cases/${encodeURIComponent(caseId)}/history`, {
      query: { offset },
    });
  }
  import(params: PaypalDisputeImportRequest): Promise<PaypalDisputeImportResponse> {
    return this.request('POST', '/disputes/paypal/import', { body: params });
  }
  /** The backend checks current provider permissions and deduplicates request_id.
   * An unknown result requires reconciliation, not a new request ID.
   */
  act(caseId: string, params: PaypalDisputeActionRequest): Promise<PaypalDisputeActionResponse> {
    return this.request('POST', `/disputes/paypal/cases/${encodeURIComponent(caseId)}/actions`, {
      body: params,
    });
  }
}
